// Gallery "Xem ảnh" (QĐ 0096 câu 6a): lễ tân mở khi tư vấn / nâng gói — mỗi
// concept của gói một khối ảnh lớn + tên + mood; bấm ảnh phóng to toàn màn hình
// (ImageLightbox, ‹ › qua mọi ảnh của concept đó). Có onSelect thì chọn được ngay.
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Button } from "@/ui/button";
import { ImageLightbox } from "@/components/ImageLightbox";
import { imageUrl } from "@/lib/upload";
import type { LookbookConcept } from "@/features/lookbook/api";
import { coverImageOf, sortImages } from "@/features/lookbook/helpers";

type Props = {
  open: boolean;
  concepts: readonly LookbookConcept[];
  onClose: () => void;
  onSelect?: ((conceptId: string, conceptName: string) => void) | undefined;
};

export function ConceptGalleryDialog({ open, concepts, onClose, onSelect }: Props) {
  // Lightbox theo concept: {conceptId, index}. Đóng = null.
  const [zoom, setZoom] = useState<{ id: string; index: number } | null>(null);
  const zoomConcept = zoom ? concepts.find((c) => c.id === zoom.id) : undefined;
  const zoomImages = zoomConcept ? sortImages(zoomConcept.images) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Radix gắn aria-labelledby vào DialogTitle nên tên truy cập của hộp là chữ
          tiêu đề (aria-label bị đè) — tiêu đề phải đúng "Xem ảnh" như test/e2e chờ. */}
      <DialogContent className="md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Xem ảnh</DialogTitle>
          <DialogDescription>Ảnh concept của gói — bấm vào ảnh để phóng to; mỗi concept có tối đa 10 ảnh.</DialogDescription>
        </DialogHeader>
        {concepts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Gói này chưa có concept nào.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {concepts.map((c) => {
              const cover = coverImageOf(c);
              const sorted = sortImages(c.images);
              return (
                <div key={c.id} className="overflow-hidden rounded-xl border bg-white">
                  {cover ? (
                    <button
                      type="button"
                      className="block aspect-[4/5] w-full bg-muted"
                      aria-label={`Phóng to ảnh ${c.name}`}
                      onClick={() => setZoom({ id: c.id, index: 0 })}
                    >
                      <img src={imageUrl(cover, "lg")} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ) : (
                    <div className="flex aspect-[4/5] w-full items-center justify-center bg-muted text-sm text-muted-foreground">
                      Chưa có ảnh
                    </div>
                  )}
                  {sorted.length > 1 && (
                    <div className="flex gap-1 overflow-x-auto px-2 pt-2">
                      {sorted.map((img, i) => (
                        <button
                          key={img.id}
                          type="button"
                          aria-label={`Ảnh ${i + 1} của ${c.name}`}
                          className="h-12 w-12 shrink-0 overflow-hidden rounded border"
                          onClick={() => setZoom({ id: c.id, index: i })}
                        >
                          <img src={imageUrl(img.url, "thumb")} alt="" loading="lazy" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{c.name}</div>
                      {c.mood && <div className="line-clamp-1 text-xs text-muted-foreground">{c.mood}</div>}
                    </div>
                    {onSelect && (
                      <Button
                        type="button"
                        size="sm"
                        aria-label={`Chọn concept ${c.name}`}
                        onClick={() => {
                          onSelect(c.id, c.name);
                          onClose();
                        }}
                      >
                        Chọn
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {zoom && zoomImages.length > 0 && (
          <ImageLightbox
            urls={zoomImages.map((i) => imageUrl(i.url, "lg"))}
            thumbUrls={zoomImages.map((i) => imageUrl(i.url, "thumb"))}
            index={zoom.index}
            onClose={() => setZoom(null)}
            onIndex={(i) => setZoom({ id: zoom.id, index: i })}
            origHref={(i) => zoomImages[i]?.url ?? ""}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
